import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  FileText, Upload, Sparkles, AlertTriangle, CheckCircle2,
  Loader2, Brain, BarChart3, Clock, Code2, XCircle, TrendingDown
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ATCAnalysis = ({ project }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);

  useEffect(() => {
    fetchReports();
  }, [project.id]);

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API}/projects/${project.id}/atc-reports`);
      console.log('Fetched ATC reports:', response.data);
      setReports(response.data || []);
    } catch (error) {
      console.error('Error fetching ATC reports:', error);
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/projects/${project.id}/atc-reports/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      console.log('Upload response:', response.data);
      
      if (response.data.success) {
        alert(`✅ ATC Report Uploaded Successfully!\n\nTotal Findings: ${response.data.total_findings}\nReport ID: ${response.data.report_id}`);
        await fetchReports();
        
        // Auto-select the newly uploaded report
        if (response.data.report_id) {
          setTimeout(() => viewReport(response.data.report_id), 500);
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload ATC report. Please try again.');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const analyzeReport = async (reportId) => {
    setAnalyzing(reportId);
    try {
      const response = await axios.post(`${API}/atc-reports/${reportId}/analyze?project_id=${project.id}`);
      console.log('Analysis response:', response.data);
      
      // Refresh reports to get updated analysis
      await fetchReports();
      
      // Update selected report with analysis
      const updatedReport = await axios.get(`${API}/atc-reports/${reportId}`);
      setSelectedReport(updatedReport.data);
      
      alert('✅ AI Analysis Complete! View the Analysis tab.');
    } catch (error) {
      console.error('Analysis error:', error);
      alert('Failed to analyze report. Please try again.');
    } finally {
      setAnalyzing(null);
    }
  };

  const viewReport = async (reportId) => {
    try {
      const response = await axios.get(`${API}/atc-reports/${reportId}`);
      console.log('Selected report:', response.data);
      setSelectedReport(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
      alert('Failed to load report details');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 via-orange-600 to-yellow-600 rounded-2xl p-8 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <FileText className="w-12 h-12" />
            <div>
              <h2 className="text-3xl font-bold">ATC Analysis</h2>
              <p className="text-orange-100 mt-1">ABAP Test Cockpit report analysis with AI-powered insights</p>
            </div>
          </div>
          <label className="px-6 py-3 bg-white text-orange-700 rounded-xl hover:bg-orange-50 transition-colors font-medium cursor-pointer flex items-center space-x-2">
            {uploading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Uploading...</span>
              </>
            ) : (
              <>
                <Upload className="w-5 h-5" />
                <span>Upload ATC Report</span>
              </>
            )}
            <input
              type="file"
              accept=".xml,.html,.json,.txt"
              onChange={handleFileUpload}
              disabled={uploading}
              className="hidden"
            />
          </label>
        </div>
      </div>

      {/* Main Content */}
      {reports.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-gray-100">
          <FileText className="w-20 h-20 mx-auto mb-6 text-gray-300" />
          <h3 className="text-2xl font-semibold text-gray-900 mb-3">No ATC Reports Uploaded</h3>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Upload ABAP Test Cockpit analysis results to get AI-powered remediation guidance
          </p>
          <label className="px-8 py-3 bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-xl hover:shadow-lg transition-all font-medium inline-flex items-center space-x-2 cursor-pointer">
            <Upload className="w-5 h-5" />
            <span>Upload First Report</span>
            <input
              type="file"
              accept=".xml,.html,.json,.txt"
              onChange={handleFileUpload}
              disabled={uploading}
              className="hidden"
            />
          </label>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Reports List */}
          <div className="lg:col-span-1 space-y-4">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Uploaded Reports ({reports.length})</h3>
            <div className="space-y-3">
              {reports.map((report) => (
                <div
                  key={report.id}
                  onClick={() => viewReport(report.id)}
                  className={`bg-white border-2 rounded-xl p-4 cursor-pointer transition-all ${
                    selectedReport?.id === report.id
                      ? 'border-blue-500 shadow-lg ring-4 ring-blue-100'
                      : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <p className="font-bold text-gray-900 truncate mb-1">{report.filename}</p>
                      <p className="text-xs text-gray-600">
                        {new Date(report.uploaded_at).toLocaleString()}
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      report.analysis_status === 'completed' ? 'bg-green-100 text-green-700' :
                      report.analysis_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {report.analysis_status}
                    </span>
                  </div>
                  
                  {report.parsed_data && report.parsed_data.findings_by_severity && (
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="bg-red-50 rounded-lg p-2 text-center">
                        <p className="text-xs text-red-600 font-medium">Critical</p>
                        <p className="text-lg font-bold text-red-700">
                          {report.parsed_data.findings_by_severity.critical || 0}
                        </p>
                      </div>
                      <div className="bg-orange-50 rounded-lg p-2 text-center">
                        <p className="text-xs text-orange-600 font-medium">High</p>
                        <p className="text-lg font-bold text-orange-700">
                          {report.parsed_data.findings_by_severity.high || 0}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Report Details */}
          <div className="lg:col-span-2">
            {selectedReport ? (
              <ReportDetails 
                report={selectedReport} 
                onAnalyze={analyzeReport}
                analyzing={analyzing === selectedReport.id}
              />
            ) : (
              <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-gray-100">
                <Code2 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h4 className="text-xl font-semibold text-gray-900 mb-2">No Report Selected</h4>
                <p className="text-gray-600">Click on a report to view detailed findings and AI analysis</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-orange-50 border-2 border-orange-200 rounded-2xl p-6">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="w-6 h-6 text-orange-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-orange-900 mb-2">How to Export ATC Results</h4>
            <ul className="text-sm text-orange-800 space-y-2">
              <li>• <strong>Transaction ATC:</strong> Run analysis, then Export → XML/HTML</li>
              <li>• <strong>Transaction SAT:</strong> Code Inspector results can be exported</li>
              <li>• <strong>Supported formats:</strong> XML (preferred), HTML, JSON, TXT</li>
              <li>• <strong>AI Analysis:</strong> Click "AI Analyze Report" to get comprehensive insights</li>
              <li>• <strong>Findings:</strong> All issues categorized by severity and type</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

const ReportDetails = ({ report, onAnalyze, analyzing }) => {
  const [activeTab, setActiveTab] = useState('summary');
  const parsedData = report.parsed_data;

  if (!parsedData) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100 text-center">
        <XCircle className="w-16 h-16 mx-auto mb-4 text-red-400" />
        <p className="text-gray-600">Report data not available or failed to parse</p>
      </div>
    );
  }

  const severityColors = {
    critical: 'bg-red-100 text-red-800 border-red-300',
    high: 'bg-orange-100 text-orange-800 border-orange-300',
    medium: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    low: 'bg-green-100 text-green-800 border-green-300'
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">{report.filename}</h3>
            <p className="text-sm text-gray-600">
              Uploaded: {new Date(report.uploaded_at).toLocaleString()}
            </p>
          </div>
          {!report.ai_analysis && (
            <button
              onClick={() => onAnalyze(report.id)}
              disabled={analyzing}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all font-medium disabled:opacity-50 flex items-center space-x-2"
            >
              {analyzing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Analyzing...</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  <span>AI Analyze Report</span>
                </>
              )}
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex space-x-2 border-b border-gray-200">
          {['summary', 'findings', 'analysis'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 font-medium text-sm transition-colors capitalize ${
                activeTab === tab
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6 max-h-[700px] overflow-y-auto">
        {activeTab === 'summary' && (
          <div className="space-y-6">
            {/* Statistics Grid */}
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-blue-50 rounded-xl p-4 border-2 border-blue-200">
                <div className="flex items-center space-x-2 mb-2">
                  <Code2 className="w-5 h-5 text-blue-600" />
                  <p className="text-sm text-blue-600 font-medium">Objects Scanned</p>
                </div>
                <p className="text-3xl font-bold text-blue-900">{parsedData.total_objects_scanned || 0}</p>
              </div>
              <div className="bg-red-50 rounded-xl p-4 border-2 border-red-200">
                <div className="flex items-center space-x-2 mb-2">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                  <p className="text-sm text-red-600 font-medium">Critical</p>
                </div>
                <p className="text-3xl font-bold text-red-900">{parsedData.findings_by_severity?.critical || 0}</p>
              </div>
              <div className="bg-orange-50 rounded-xl p-4 border-2 border-orange-200">
                <div className="flex items-center space-x-2 mb-2">
                  <AlertTriangle className="w-5 h-5 text-orange-600" />
                  <p className="text-sm text-orange-600 font-medium">High</p>
                </div>
                <p className="text-3xl font-bold text-orange-900">{parsedData.findings_by_severity?.high || 0}</p>
              </div>
              <div className="bg-yellow-50 rounded-xl p-4 border-2 border-yellow-200">
                <div className="flex items-center space-x-2 mb-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-600" />
                  <p className="text-sm text-yellow-600 font-medium">Medium</p>
                </div>
                <p className="text-3xl font-bold text-yellow-900">{parsedData.findings_by_severity?.medium || 0}</p>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-6">
                <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  <Clock className="w-5 h-5 mr-2 text-purple-600" />
                  Effort Estimation
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">Total Remediation:</span>
                    <span className="text-2xl font-bold text-purple-900">{parsedData.summary?.total_remediation_hours || 0}h</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700">Affected Objects:</span>
                    <span className="text-2xl font-bold text-purple-900">{parsedData.summary?.affected_objects || 0}</span>
                  </div>
                  <div className="pt-3 border-t border-purple-200">
                    <p className="text-sm text-gray-600">Estimated Timeline: {Math.ceil((parsedData.summary?.total_remediation_hours || 0) / 8)} days</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-2 border-blue-200 rounded-xl p-6">
                <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                  <BarChart3 className="w-5 h-5 mr-2 text-blue-600" />
                  Findings by Type
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700 flex items-center">
                      <XCircle className="w-4 h-4 mr-2 text-red-600" />
                      Errors:
                    </span>
                    <span className="text-2xl font-bold text-red-600">{parsedData.findings_by_type?.error || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700 flex items-center">
                      <AlertTriangle className="w-4 h-4 mr-2 text-orange-600" />
                      Warnings:
                    </span>
                    <span className="text-2xl font-bold text-orange-600">{parsedData.findings_by_type?.warning || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-700 flex items-center">
                      <CheckCircle2 className="w-4 h-4 mr-2 text-blue-600" />
                      Info:
                    </span>
                    <span className="text-2xl font-bold text-blue-600">{parsedData.findings_by_type?.info || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'findings' && (
          <div className="space-y-4">
            <h4 className="text-lg font-bold text-gray-900 mb-4">Detailed Findings ({parsedData.findings?.length || 0})</h4>
            {parsedData.findings && parsedData.findings.length > 0 ? (
              parsedData.findings.map((finding) => (
                <div key={finding.id} className={`border-2 rounded-xl p-5 ${severityColors[finding.severity]}`}>
                  {/* Finding Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="font-mono text-sm font-bold bg-gray-900 text-white px-3 py-1 rounded">
                          {finding.object_name}
                        </span>
                        <span className="px-2 py-1 bg-white rounded text-xs font-semibold">
                          {finding.object_type}
                        </span>
                      </div>
                      <p className="font-bold text-gray-900 mb-1">{finding.check_title}</p>
                      <p className="text-sm text-gray-800 leading-relaxed">{finding.message}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ml-3 ${
                      finding.finding_type === 'Error' ? 'bg-red-200 text-red-900' :
                      finding.finding_type === 'Warning' ? 'bg-orange-200 text-orange-900' :
                      'bg-blue-200 text-blue-900'
                    }`}>
                      {finding.finding_type}
                    </span>
                  </div>

                  {/* Code Snippet */}
                  {finding.code_snippet && (
                    <div className="mt-3 p-4 bg-gray-900 rounded-lg">
                      <p className="text-xs text-gray-400 mb-2">Line {finding.line_number}</p>
                      <code className="text-sm text-green-400 font-mono">{finding.code_snippet}</code>
                    </div>
                  )}

                  {/* Remediation & Effort */}
                  <div className="mt-4 flex items-center justify-between pt-4 border-t border-gray-300">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-700 mb-1">Remediation:</p>
                      <p className="text-sm text-gray-900">{finding.remediation}</p>
                    </div>
                    <div className="ml-4 text-right">
                      <p className="text-xs text-gray-600">Estimated Effort</p>
                      <p className="text-2xl font-bold text-gray-900">{finding.effort_hours}h</p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12 text-gray-500">
                <p>No findings available in this report</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'analysis' && (
          <div>
            {report.ai_analysis ? (
              <div>
                <div className="flex items-center space-x-3 mb-4">
                  <Brain className="w-6 h-6 text-purple-600" />
                  <h4 className="text-lg font-bold text-gray-900">AI Analysis Results</h4>
                  <span className="flex items-center space-x-1 px-3 py-1 bg-green-100 text-green-700 rounded-lg text-xs font-semibold">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Analysis Complete</span>
                  </span>
                </div>
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-6">
                  <pre className="text-sm text-gray-800 whitespace-pre-wrap font-sans leading-relaxed">
                    {report.ai_analysis}
                  </pre>
                </div>
                <div className="mt-4 text-sm text-gray-600 text-center">
                  Analyzed: {report.analyzed_at ? new Date(report.analyzed_at).toLocaleString() : 'Just now'}
                </div>
              </div>
            ) : (
              <div className="text-center py-16">
                <Sparkles className="w-20 h-20 mx-auto mb-6 text-gray-300" />
                <h4 className="text-2xl font-semibold text-gray-900 mb-3">No AI Analysis Yet</h4>
                <p className="text-gray-600 mb-8 max-w-md mx-auto">
                  Generate a comprehensive AI-powered analysis of all findings with remediation guidance, 
                  effort estimates, and priority recommendations.
                </p>
                <button
                  onClick={() => onAnalyze(report.id)}
                  disabled={analyzing}
                  className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-xl transition-all font-medium disabled:opacity-50 inline-flex items-center space-x-2 text-lg"
                >
                  {analyzing ? (
                    <>
                      <Loader2 className="w-6 h-6 animate-spin" />
                      <span>Analyzing with AI...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-6 h-6" />
                      <span>Generate AI Analysis</span>
                    </>
                  )}
                </button>
                <p className="text-sm text-gray-500 mt-4">This will take 10-15 seconds</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
